import React, { useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "src/components/ui/sheet";
import { Button } from "src/components/ui/button";
import { DeployNotification } from "../../types/deploy-notification.types";
import { useLazyGetCreativeVariantQuery } from "../../endpoints/deploy-notifications.endpoints";
import { Download, CheckCircle, Trash2 } from "lucide-react";

function formatDateOnly(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

async function downloadImageAsFile(url: string, filename?: string): Promise<void> {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("Failed to fetch image");
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename ?? "creative.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

interface DeployRequestDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notification: DeployNotification | null;
  onMarkComplete?: (notification: DeployNotification) => void;
  onDelete?: (notification: DeployNotification) => void;
}

const DeployRequestDrawer: React.FC<DeployRequestDrawerProps> = ({
  open,
  onOpenChange,
  notification,
  onMarkComplete,
  onDelete,
}) => {
  const [fetchCreativeVariant, { data: creativeVariant }] = useLazyGetCreativeVariantQuery();
  const mediaUrl = creativeVariant?.media_url ?? null;

  useEffect(() => {
    if (open && notification?.creative_variant) {
      fetchCreativeVariant(notification.creative_variant);
    }
  }, [open, notification?.creative_variant, fetchCreativeVariant]);

  const handleDownload = useCallback(async() => {
    if (!mediaUrl) return;
    try {
      await downloadImageAsFile(
        mediaUrl,
        `creative-${notification?.id ?? "image"}.png`
      );
    } catch {
      // CORS or network error: fallback to opening in new tab
      window.open(mediaUrl, "_blank", "noopener,noreferrer");
    }
  }, [mediaUrl, notification?.id]);

  if (!open) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="!w-[90vw] sm:!max-w-xl overflow-y-auto bg-white"
      >
        {notification ? (
          <>
            <SheetHeader className="sr-only">
              <SheetTitle>
                Request details
              </SheetTitle>
            </SheetHeader>

            {notification.is_complete && (
              <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-2 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <span className="text-sm font-medium text-green-800">
                  This request is complete.
                </span>
              </div>
            )}

            {/* Creative – most prominent */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Creative
              </h3>
              <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center min-h-[200px]">
                {mediaUrl ? (
                  <img
                    src={mediaUrl}
                    alt="Creative"
                    className="max-h-[320px] w-auto object-contain"
                  />
                ) : (
                  <p className="text-gray-500 py-8">
                    Image loading…
                  </p>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  disabled={!mediaUrl}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download image
                </Button>
              </div>
            </section>

            {/* Start & end date – prominent */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Dates
              </h3>
              <div className="flex flex-wrap gap-6 text-gray-900">
                <div>
                  <span className="text-gray-500 text-sm block">
                    Start date
                  </span>
                  <span className="font-medium">
                    {formatDateOnly(notification.start_date)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 text-sm block">
                    End date
                  </span>
                  <span className="font-medium">
                    {formatDateOnly(notification.end_date)}
                  </span>
                </div>
              </div>
            </section>

            {/* Audience description – prominent */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Audience
              </h3>
              <p className="text-gray-900 whitespace-pre-wrap">
                {notification.audience_description ?? "—"}
              </p>
            </section>

            {/* Secondary details */}
            <section className="mb-6 text-sm text-gray-600">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Details
              </h3>
              <dl className="grid gap-2">
                <div>
                  <dt className="text-gray-500">
                    Status
                  </dt>
                  <dd className="flex items-center gap-2">
                    {notification.is_complete ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                        Complete
                      </>
                    ) : notification.status === "archived" ? (
                      "Archived"
                    ) : (
                      "Pending"
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">
                    User ID
                  </dt>
                  <dd className="font-mono text-xs">
                    {notification.user ?? "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">
                    Creative variant ID
                  </dt>
                  <dd className="font-mono text-xs">
                    {notification.creative_variant ?? "—"}
                  </dd>
                </div>
                {notification.details &&
                Object.keys(notification.details).length > 0 && (
                  <div>
                    <dt className="text-gray-500">
                      Extra details
                    </dt>
                    <dd className="font-mono text-xs break-all">
                      {JSON.stringify(notification.details)}
                    </dd>
                  </div>
                )}
              </dl>
            </section>

            {/* Actions */}
            <section className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                onClick={() => onMarkComplete?.(notification)}
                disabled={notification.is_complete}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Mark as complete
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onDelete?.(notification)}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </section>
          </>
        ) : (
          <p className="text-gray-500">
            Select a request to view details.
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DeployRequestDrawer;
